import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import type {
  AdminUserRow,
  AllowListRow,
  AuditRow,
  CreateAllowListEntryRequest,
  UpdateAllowListEntryRequest,
} from "@shared/schemas/admin";
import type { Collection } from "@shared/schemas/common";
import {
  createAllowListEntry,
  listAdminUsers,
  listAllowList,
  listAuditLog,
  revokeAllowListEntry,
  updateAllowListEntry,
  type AdminUserFilters,
  type AllowListFilters,
  type AuditFilters,
} from "@client/lib/adminApi";
import { queryKeys } from "@client/lib/queryClient";

/**
 * Admin reads and writes as hooks.
 *
 * Every allow-list mutation invalidates the whole admin tree rather than patching
 * the row it changed. The three screens are views of the same handful of rows —
 * revoking an entry changes its status here, the user's `allowListed` flag there,
 * and adds a line to the audit log — so refetching all of it is both correct and
 * cheap at this size, where trying to be clever is neither.
 */

export function useAllowList(filters: AllowListFilters): UseQueryResult<Collection<AllowListRow>> {
  return useQuery({
    queryKey: queryKeys.admin.allowList(filters),
    queryFn: () => listAllowList(filters),
    // Typing in the search box narrows the list in place instead of blanking it.
    placeholderData: (previous) => previous,
  });
}

export function useAdminUsers(filters: AdminUserFilters): UseQueryResult<Collection<AdminUserRow>> {
  return useQuery({
    queryKey: queryKeys.admin.users(filters),
    queryFn: () => listAdminUsers(filters),
    placeholderData: (previous) => previous,
  });
}

export function useAuditLog(filters: AuditFilters): UseQueryResult<Collection<AuditRow>> {
  return useQuery({
    queryKey: queryKeys.admin.audit(filters),
    queryFn: () => listAuditLog(filters),
    placeholderData: (previous) => previous,
  });
}

function useAdminInvalidate() {
  const client = useQueryClient();
  return () => client.invalidateQueries({ queryKey: queryKeys.admin.all });
}

export function useAddAllowListEntry() {
  const invalidate = useAdminInvalidate();
  return useMutation({
    mutationFn: (input: CreateAllowListEntryRequest) => createAllowListEntry(input),
    onSuccess: () => void invalidate(),
  });
}

export function useUpdateAllowListEntry() {
  const invalidate = useAdminInvalidate();
  return useMutation({
    mutationFn: (input: { id: string } & UpdateAllowListEntryRequest) => {
      const { id, ...changes } = input;
      return updateAllowListEntry(id, changes);
    },
    onSuccess: () => void invalidate(),
  });
}

export function useRevokeAllowListEntry() {
  const invalidate = useAdminInvalidate();
  return useMutation({
    mutationFn: (id: string) => revokeAllowListEntry(id),
    onSuccess: () => void invalidate(),
  });
}
